import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

const BASE = "/v1/puzzle/jigsaw/template";

/** GET may return `{ data: JigsawTemplateRo }`; prefer inner record when outer lacks template fields. */
function unwrapTemplateGetResponse(response) {
    if (!response || typeof response !== "object") return response;
    const inner = response.data;
    if (!inner || typeof inner !== "object" || Array.isArray(inner)) return response;
    const outerIsTemplate =
        typeof response.id === "string" ||
        typeof response.name === "string" ||
        response.tileSize != null ||
        response.tile_size != null;
    return outerIsTemplate ? response : { ...response, ...inner };
}

/** Match list page: only explicit false disables. */
export function isJigsawTemplateEnabled(tpl) {
    if (!tpl || typeof tpl !== "object") return true;
    const raw = tpl.enabled;
    if (raw === false || raw === "false" || raw === 0 || raw === "0") return false;
    return true;
}

/** Hidden from /home/jigsaw when true (admin list still shows the row). */
export function isJigsawTemplateInvisible(tpl) {
    if (!tpl || typeof tpl !== "object") return false;
    const raw = tpl.invisible;
    return raw === true || raw === "true" || raw === 1 || raw === "1";
}

const AdminJigsawTemplateService = {
    /** GET /v1/puzzle/jigsaw/template — paginated list */
    list: async (page = 0, pageSize = 20, sort = "name:desc") => {
        try {
            const response = await ApiService.getData(BASE, { page, pageSize, sort });
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            const content = response?.content ?? [];
            return {
                success: true,
                data: {
                    content,
                    totalElements: response?.totalElements ?? response?.page?.totalElements ?? content.length,
                    totalPages: response?.totalPages ?? response?.page?.totalPages ?? 0,
                },
            };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.list:", e);
            return { success: false, error: e.message };
        }
    },

    /** GET /v1/puzzle/jigsaw/template/{id} */
    getById: async (id) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.getData(`${BASE}/${encodeURIComponent(id)}`);
            if (response?.code === "NO_DATA") return { success: false, code: "NO_DATA", error: response.message || "Not found" };
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: unwrapTemplateGetResponse(response) };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.getById:", e);
            return { success: false, error: e.message };
        }
    },

    /** POST /v1/puzzle/jigsaw/template — create */
    create: async ({ gridSize, svgPath, piecePaths }) => {
        try {
            if (!gridSize || !svgPath || !piecePaths) return { success: false, error: "gridSize, svgPath, piecePaths required" };
            const response = await ApiService.postData(BASE, { gridSize, svgPath, piecePaths });
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.create:", e);
            return { success: false, error: e.message };
        }
    },

    /** PUT /v1/puzzle/jigsaw/template — update (id required + at least one of gridSize, svgPath, piecePaths) */
    update: async (fields = {}) => {
        try {
            if (!fields.id) return { success: false, error: "Id required" };
            const response = await ApiService.putData(BASE, fields);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.update:", e);
            return { success: false, error: e.message };
        }
    },

    /** PUT /v1/puzzle/jigsaw/template/{id}/enabled */
    setEnabled: async (id, enabled) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.putData(
                `${BASE}/${encodeURIComponent(id)}/enabled`,
                { enabled: Boolean(enabled) },
            );
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: unwrapTemplateGetResponse(response) };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.setEnabled:", e);
            return { success: false, error: e.message };
        }
    },

    /** PUT /v1/puzzle/jigsaw/template/{id}/invisible */
    setInvisible: async (id, invisible) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.putData(
                `${BASE}/${encodeURIComponent(id)}/invisible`,
                { invisible: Boolean(invisible) },
            );
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: unwrapTemplateGetResponse(response) };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.setInvisible:", e);
            return { success: false, error: e.message };
        }
    },

    /** DELETE /v1/puzzle/jigsaw/template/{id} */
    remove: async (id) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.deleteData(`${BASE}/${encodeURIComponent(id)}`);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.remove:", e);
            return { success: false, error: e.message };
        }
    },

    /**
     * POST /v1/puzzle/jigsaw/template/{id}/ensure-stories?size=
     * Backfill this template onto eligible REVIEWED/PUBLISHED stories that already have jigsaw rows.
     * Allowed while template is disabled (prep before enable). Omit size to process all matching candidates.
     */
    ensureStories: async (id, size) => {
        try {
            if (!id) return { success: false, error: "Id required", data: null };
            let url = `${BASE}/${encodeURIComponent(id)}/ensure-stories`;
            const rawSize = size != null ? String(size).trim() : "";
            if (rawSize !== "") {
                const n = Number(rawSize);
                if (!Number.isFinite(n)) {
                    return { success: false, error: "Batch size must be a number", data: null };
                }
                const clamped = Math.min(1000, Math.max(1, Math.round(n)));
                url += `?size=${encodeURIComponent(clamped)}`;
            }
            const response = await ApiService.postData(url, {});
            if (response?.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Backfill failed",
                    data: null,
                };
            }
            return {
                success: true,
                data: {
                    templateId: response?.templateId ?? response?.data?.templateId ?? id,
                    candidates: response?.candidates ?? response?.data?.candidates,
                    processed: response?.processed ?? response?.data?.processed,
                    created: response?.created ?? response?.data?.created ?? 0,
                    results: response?.results ?? response?.data?.results ?? [],
                    errors: response?.errors ?? response?.data?.errors ?? [],
                    raw: response,
                },
            };
        } catch (e) {
            Logger.error("AdminJigsawTemplateService.ensureStories:", e);
            return { success: false, error: e.message || "Backfill failed", data: null };
        }
    },
};

export default AdminJigsawTemplateService;
